import { ref, onUnmounted } from 'vue';
import { debounce } from '@/helpers/debounceHelper';

export interface AutoCompleteOption {
  value: string | number;
  label: string;
}

export interface FetchOptionsResult {
  options: AutoCompleteOption[];
  hasMore: boolean;
}

export type FetchOptionsFn = (
  term: string,
  signal: AbortSignal
) => Promise<FetchOptionsResult>;

export function useAutoCompleteOptions (
  fetchOptionsFn: FetchOptionsFn,
  options: { debounceMs?: number } = {}
) {
  const { debounceMs = 300 } = options;
  const optionsList = ref<AutoCompleteOption[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);
  const hasMore = ref(false);

  let abortController: AbortController | null = null;
  let disposed = false;

  const debouncedFetch = debounce((term: string) => doFetch(term), debounceMs);

  function doFetch (term: string): void {
    if (disposed) return;

    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const controller = abortController;
    loading.value = true;
    error.value = null;

    fetchOptionsFn(term, controller.signal)
      .then((result: FetchOptionsResult) => {
        if (disposed) return;
        optionsList.value = result.options ?? [];
        hasMore.value = result.hasMore ?? false;
      })
      .catch((err: unknown) => {
        const isAbort =
          err instanceof Error &&
          (err.name === 'AbortError' || (err as { code?: string }).code === 'ERR_CANCELED');
        if (!isAbort && !disposed) {
          error.value = err instanceof Error ? err : new Error(String(err));
        }
      })
      .finally(() => {
        if (disposed) {
          if (controller === abortController) abortController = null;
          return;
        }
        if (abortController === controller) {
          abortController = null;
          loading.value = false;
        }
      });
  }

  function requestOptions (term: string): void {
    if (term === '') {
      doFetch('');
    } else {
      debouncedFetch(term);
    }
  }

  function cleanup (): void {
    disposed = true;
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    loading.value = false;
  }

  onUnmounted(cleanup);

  return {
    options: optionsList,
    loading,
    error,
    hasMore,
    fetch: requestOptions,
    cleanup
  };
}

